<div class="flex h-full w-full flex-col gap-3">

  @if (loading) {
  <div class="flex items-center justify-center py-16">
    <p-progressSpinner styleClass="h-12 w-12" />
  </div>
  } @else if (loadError) {
  <div class="rounded border border-red-300 bg-red-50 p-4 text-red-700">
    <div class="font-semibold">Could not load the offering</div>
    <div class="text-sm">{{ loadError }}</div>
  </div>
  } @else {

  <div class="text-sm text-gray-600">
    <b>{{ params.productKey }}</b> — {{ params.productTypeName }}
    <!-- Feed vocabulary, shown only where a feed exists. 76 of 78 manufacturers have none, and
         "0 of 1 colors matched to supplier images" against one of those is reporting a
         reconciliation failure that cannot happen. -->
    @if (isFeedEnabled && colors.length) {
    <span class="ml-2 rounded px-2 py-0.5 text-xs font-semibold"
      [class]="matchedCount === colors.length ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'">
      {{ matchedCount }} of {{ colors.length }} colors matched to supplier images
    </span>
    }
    @if (imageError) {
    <span class="ml-2 rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
      supplier images unavailable
    </span>
    }
  </div>

  <div class="flex gap-5">

    <!-- Main image, changes with the selected color -->
    <div class="flex w-1/2 flex-col items-center justify-start">
      @if (selectedColor?.imageUrl) {
      <img class="main-image w-full" [src]="selectedView ?? selectedColor!.imageUrl"
           [alt]="selectedColor!.name" />

      <!-- Other views of this same color. Hidden entirely when the supplier publishes none,
           which is most products - an empty strip would read as something failing to load. -->
      @if (selectedColor!.additionalUrls.length) {
      <div class="mt-2 flex flex-wrap justify-center gap-2">
        <img class="h-14 w-14 cursor-pointer rounded border object-cover"
             [class.border-blue-500]="selectedView === null"
             [class.border-gray-200]="selectedView !== null"
             [src]="selectedColor!.imageUrl" [alt]="selectedColor!.name + mainViewLabel"
             [title]="selectedColor!.name + mainViewLabel"
             (click)="selectedView = null" />
        @for (u of selectedColor!.additionalUrls; track u) {
        <img class="h-14 w-14 cursor-pointer rounded border object-cover"
             [class.border-blue-500]="selectedView === u"
             [class.border-gray-200]="selectedView !== u"
             [src]="u" [alt]="selectedColor!.name" (click)="selectedView = u" />
        }
      </div>
      }

      <div class="mt-2 text-sm font-semibold">{{ selectedColor!.name }}</div>
      @if (isFeedEnabled && selectedColor!.imageSource === 'Stored') {
      <div class="mt-1 text-xs text-gray-500">
        the supplier published no picture for this color &mdash; showing the style image we hold
      </div>
      }
      @if (isFeedEnabled && selectedColor!.supplierColorCode && selectedColor!.supplierColorCode !== selectedColor!.name) {
      <div class="mt-1 text-xs text-gray-500">
        supplier code <b>{{ selectedColor!.supplierColorCode }}</b>
        @if (selectedColor!.pms) { · {{ selectedColor!.pms }} }
        @if (selectedColor!.matchedOn === 'ColorCode') {
        <span class="ml-1 text-amber-700">matched on the catalogue token, not the standard name</span>
        }
      </div>
      }
      @if (isFeedEnabled && selectedColor!.ambiguousCodes) {
      <div class="mt-1 text-xs text-amber-700">
        supplier publishes several codes under this name — {{ selectedColor!.ambiguousCodes }}
      </div>
      }
      } @else if (selectedColor && isFeedEnabled) {
      <div class="flex h-64 w-full items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50
                  text-center text-sm text-gray-500">
        No supplier image for<br /><b>{{ selectedColor.name }}</b>
        <div class="mt-2 text-xs">the supplier publishes no color of this name on this style</div>
      </div>
      } @else if (selectedColor) {
      <!-- No feed covers this manufacturer, so there is nothing to explain. The same placeholder
           the Job Order Detail dialog uses, and no feed wording anywhere. -->
      <img class="main-image w-full" src="/assets/proximity/no-thumbnail.png" alt="No image" />
      } @else {
      <div class="flex h-64 w-full items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50
                  text-sm text-gray-500">
        Choose a color
      </div>
      }
    </div>

    <!-- Selection -->
    <div class="flex w-1/2 flex-col gap-4">

      <!-- NOTHING TO CHOOSE IS NOT A CHOICE OF ONE.
           A product type with no color feature, under a manufacturer no feed covers, has no
           color dimension at all - so offering a swatch and a dropdown holding the single
           invented entry "One color" asks a question that does not exist. Where a feed DOES
           cover it, the group is named from the supplier's own color and is worth showing. -->
      @if (hasColorChoice) {
      <div>
        <div class="mb-1 text-xs font-semibold uppercase text-gray-500">
          Color
          @if (selectedSize) {
          <span class="ml-1 normal-case text-gray-400">— available in {{ selectedSize }}</span>
          }
        </div>

        <!-- Chips -->
        <div class="mb-2 flex flex-wrap gap-2">
          @for (c of colors; track c.name) {
          <div class="chip" [class.chip-selected]="selectedColor?.name === c.name"
            [class.chip-unavailable]="!isColorAvailable(c)"
            [title]="c.name + (c.supplierColorCode ? ' (' + c.supplierColorCode + ')' : '') + ' — ' + c.sizes.join(', ')"
            (click)="onSelectColor(c)">
            @if (c.swatchUrl) {
            <img [src]="c.swatchUrl" [alt]="c.name" />
            } @else {
            <div class="flex h-full w-full items-center justify-center text-[9px] text-gray-500">n/a</div>
            }
          </div>
          }
        </div>

        <!-- Same choice, as a dropdown -->
        <p-select [options]="colors" [(ngModel)]="selectedColor" optionLabel="name" placeholder="Choose a color"
          styleClass="w-full" [showClear]="true" />
      </div>
      }

      <div>
        <div class="mb-1 text-xs font-semibold uppercase text-gray-500">
          {{ sizeFeatureName }}
          @if (selectedColor) {
          <span class="ml-1 normal-case text-gray-400">— {{ selectedColor.sizes.length }} offered in
            {{ selectedColor.name }}</span>
          }
        </div>
        <p-select [options]="availableSizes" [(ngModel)]="selectedSize" placeholder="Choose a size"
          styleClass="w-full" [showClear]="true" (onChange)="onSelectSize()" />
      </div>

      <!-- What the config actually offers, which is the thing being proved -->
      <div class="rounded border border-gray-200 bg-gray-50 p-3 text-xs">
        <div class="mb-1 font-semibold text-gray-600">This configuration offers</div>
        @for (c of colors; track c.name) {
        <div class="flex justify-between gap-3 py-0.5">
          <span [class.font-semibold]="selectedColor?.name === c.name">{{ c.name }}</span>
          <span class="text-gray-500">{{ c.sizes.join(', ') }}</span>
        </div>
        } @empty {
        <div class="py-0.5 text-gray-500">Nothing in this configuration is available right now.</div>
        }
      </div>

    </div>
  </div>

  <div class="qb-page-footer mt-auto flex justify-end">
    <button pButton type="button" icon="pi pi-times" label="Close" class="p-button-raised" (click)="close()"></button>
  </div>

  }
</div>
